import { useForm } from 'react-hook-form';
import { CYCLE_LABEL, TIMING_LABEL, VATID_LABEL, invoiceRows, type Service } from '@biuro/shared';
import { useData, useDeleteDoc, useSaveDoc, useToast } from '../hooks.tsx';
import { DeleteButton, Sheet } from '../ui.tsx';

type Form = Omit<Service, 'vatRate'> & { vatRate: string };

export function ServiceForm({ id, onClose }: { id?: string; onClose: () => void }) {
  const { data } = useData();
  const save = useSaveDoc();
  const del = useDeleteDoc();
  const toast = useToast();
  const s = id ? data.services[id] : undefined;
  const used = id ? invoiceRows(data).filter((x) => x.inv.serviceId === id).length : 0;
  const { register, handleSubmit, formState: { errors } } = useForm<Form>({
    defaultValues: {
      name: '', vendor: '', country: '', vendorVat: '', currency: '', cycle: 'monthly', timing: 'prepaid', vatIdSet: 'unknown',
      since: '', invoiceSource: '', senders: '', notes: '', active: true, verify: false, cardAutopay: false,
      ...s, vatRate: String(s?.vatRate ?? 23),
    },
  });

  const submit = handleSubmit((v) => {
    const doc: Service = {
      ...v, name: v.name.trim(), country: (v.country || '').trim().toUpperCase(), currency: (v.currency || '').trim().toUpperCase(),
      vatRate: Number(v.vatRate), since: v.since || '',
    };
    save.mutate({ col: 'services', id: id || 'svc-' + Date.now().toString(36), doc });
    toast('Zapisano usługę');
    onClose();
  });

  return (
    <Sheet title={s?.name || 'Nowa usługa'} onClose={onClose}
      footer={<>
        {id ? (used ? <span className="hint">Usługa ma faktury – zamiast usuwać, odznacz „Aktywna”.</span>
          : <DeleteButton onConfirm={() => { del.mutate({ col: 'services', id }); onClose(); }} />) : <span />}
        <div className="r"><button className="btn" onClick={onClose}>Anuluj</button><button className="btn pri" onClick={submit}>Zapisz</button></div>
      </>}>
      <form className="fgrid" onSubmit={submit}>
        <div className="fld">
          <label htmlFor="v-name">Nazwa (jak ją nazywasz)</label>
          <input id="v-name" autoComplete="off" {...register('name', { required: true })} />
          {errors.name && <div className="hint err">Podaj nazwę.</div>}
        </div>
        <div className="fld"><label htmlFor="v-vendor">Podmiot na fakturze</label><input id="v-vendor" placeholder="np. Anthropic Ireland, Limited" {...register('vendor')} /></div>
        <div className="fld"><label htmlFor="v-country">Kraj dostawcy (kod)</label><input id="v-country" maxLength={2} placeholder="IE" className="upper" {...register('country')} /></div>
        <div className="fld"><label htmlFor="v-vat">Numer VAT dostawcy</label><input id="v-vat" {...register('vendorVat')} /></div>
        <div className="fld"><label htmlFor="v-cur">Waluta faktur</label><input id="v-cur" maxLength={3} placeholder="USD" className="upper" {...register('currency')} /></div>
        <div className="fld">
          <label htmlFor="v-vr">Stawka VAT</label>
          <select id="v-vr" {...register('vatRate')}><option value="23">23%</option><option value="8">8%</option><option value="5">5%</option></select>
        </div>
        <div className="fld">
          <label htmlFor="v-cycle">Jak często faktura</label>
          <select id="v-cycle" {...register('cycle')}>{Object.entries(CYCLE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </div>
        <div className="fld">
          <label htmlFor="v-timing">Płatność</label>
          <select id="v-timing" {...register('timing')}>{Object.entries(TIMING_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </div>
        <div className="fld">
          <label htmlFor="v-vatid">Twój VAT-UE u dostawcy</label>
          <select id="v-vatid" {...register('vatIdSet')}>{Object.entries(VATID_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </div>
        <div className="fld"><label htmlFor="v-since">Faktury od miesiąca</label><input id="v-since" type="month" {...register('since')} /></div>
        <div className="fld full">
          <label htmlFor="v-senders">Nadawcy maili z fakturami (Gmail)</label>
          <input id="v-senders" placeholder="np. invoice+statements@openai.com, billing@elementor.com" autoComplete="off" {...register('senders')} />
          <div className="hint">Adresy albo domeny oddzielone przecinkami. Faktury z załącznikiem PDF od tych nadawców trafią na Dysk jako szkice.</div>
        </div>
        <div className="fld full"><label htmlFor="v-src">Gdzie pobierasz faktury</label><input id="v-src" placeholder="np. Ustawienia → Konto → Faktury" {...register('invoiceSource')} /></div>
        <div className="fld full"><label htmlFor="v-notes">Notatki</label><textarea id="v-notes" {...register('notes')} /></div>
        <label className="chk full"><input type="checkbox" {...register('active')} /> Aktywna (sprawdzam brakujące faktury)</label>
        <label className="chk full"><input type="checkbox" {...register('cardAutopay')} /> Płacę kartą automatycznie w dniu faktury</label>
        <label className="chk full"><input type="checkbox" {...register('verify')} /> Dane do uzupełnienia</label>
      </form>
    </Sheet>
  );
}
